/**
 * AuthLayout - A template component for authentication pages.
 *
 * Provides a consistent, centered layout for login and password reset pages.
 * Combines organisms (LoginForm) with layout structure.
 */

import { type ReactNode, type HTMLAttributes } from 'react';
import { cn } from '@lean-agility/design-system/react';
import { Card, CardHeader, CardTitle, CardContent } from '@lean-agility/design-system/react';

export interface AuthLayoutProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The main content to display (typically a form organism).
   */
  children: ReactNode;

  /**
   * Title displayed in the card header.
   */
  title: string;

  /**
   * Optional subtitle or description text displayed below the title.
   */
  subtitle?: string;

  /**
   * Optional footer content (e.g., "Forgot your password?").
   */
  footer?: ReactNode;
}

export function AuthLayout({ children, title, subtitle, footer, className, ...props }: AuthLayoutProps) {
  return (
    <div
      className={cn(
        'flex min-h-screen items-center justify-center p-4',
        `
          bg-bg-base
          dark:bg-bg-base
        `,
        className,
      )}
      {...props}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">{title}</CardTitle>
          {subtitle && (
            <p
              className="
                text-sm text-text-muted
                dark:text-text-muted
              "
            >
              {subtitle}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {children}
          {footer && (
            <div
              className="
                mt-6 text-center text-sm text-text-muted
                dark:text-text-muted
              "
            >
              {footer}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
